import AddIcon from '@mui/icons-material/Add'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select, TextField, Tooltip } from '@mui/material'
import React from 'react'
import { Link } from 'react-router-dom'
import GrantContent from '../../../components/GrantContent'
import { listSearchSx, rowActionSx, rowDangerActionSx } from '../../../components/list-styles'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import * as Permisos from '../../../utils/Permisos'
import { formatCount } from '../common/insurer-common'
import { PAGE_SIZES, useInsurerListModel } from './insurer-list-model'
import type { InsurerRow } from './insurer-list-model'

const SKELETON_ROWS = [1, 2, 3, 4, 5, 6]
const columns = [
    { label: 'Aseguradora', className: 'min-w-0 md:min-w-[260px]' },
    { label: 'Tipo', className: 'hidden whitespace-nowrap md:table-cell' },
    { label: 'Capacidades', className: 'hidden whitespace-nowrap lg:table-cell' },
    { label: 'Estado', className: 'hidden whitespace-nowrap md:table-cell' },
    { label: 'Acciones', className: 'hidden whitespace-nowrap text-right md:table-cell', sr: true },
]

const smallSearchSx = { ...(listSearchSx as object), maxWidth: 200 }
const dialogBtnClass = 'inline-flex h-10 items-center gap-2 rounded-xl px-4 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-40'

const Capability = ({ on, icon, label }: { on: boolean, icon: React.ReactNode, label: string }) => (
    <Tooltip title={on ? `Cuenta con ${label}` : `Sin ${label}`}>
        <span className={`inline-flex h-7 w-7 items-center justify-center rounded-lg ${on ? 'bg-k-ok-soft text-k-ok' : 'bg-k-line-soft text-k-muted-2'}`}>{icon}</span>
    </Tooltip>
)

const RowActions = ({ row, onEdit, onDelete }: { row: InsurerRow, onEdit: () => void, onDelete: () => void }) => (
    row.custom ? (
        <div className="inline-flex justify-end gap-2">
            <GrantContent permisos={[Permisos.ASEGURADORAS_W]}>
                <Button
                    variant="outlined"
                    aria-label={`Editar ${row.name}`}
                    sx={rowActionSx}
                    onClick={(event) => {
                        event.stopPropagation()
                        onEdit()
                    }}
                >
                    Editar
                </Button>
            </GrantContent>
            <GrantContent permisos={[Permisos.ASEGURADORAS_D]}>
                <Button
                    variant="outlined"
                    aria-label={`Eliminar ${row.name}`}
                    sx={rowDangerActionSx}
                    onClick={(event) => {
                        event.stopPropagation()
                        onDelete()
                    }}
                >
                    Eliminar
                </Button>
            </GrantContent>
        </div>
    ) : (
        <Tooltip title="Las aseguradoras predefinidas no se pueden editar">
            <span className="inline-flex h-9 items-center gap-1 px-2 text-xs font-semibold text-k-muted-2">
                <LockOutlinedIcon style={{ fontSize: 16 }} />
                Predefinida
            </span>
        </Tooltip>
    )
)

const InsurerList = () => {
    const model = useInsurerListModel()

    const tableHead = (
        <thead>
            <tr className="text-left text-xs font-bold uppercase tracking-[0.05em] text-k-muted">
                {columns.map((column) => (
                    <th
                        key={column.label}
                        className={`sticky top-0 z-10 bg-k-surface px-2.5 py-3 font-bold ${column.className} ${column.label === 'Aseguradora' ? 'pl-4' : ''} ${column.sr ? 'pr-4 text-right' : ''}`}
                    >
                        {column.sr ? <span className="sr-only">{column.label}</span> : column.label}
                    </th>
                ))}
            </tr>
        </thead>
    )

    return (
        <KarteraAdminTemplate loading={false} padded={false} title="Aseguradoras">
            <Dialog open={model.deleteOpen} onClose={model.cancelDelete} slotProps={{ paper: { className: 'k-dialog' } }}>
                <DialogTitle>Eliminar aseguradora</DialogTitle>
                <DialogContent>
                    <p className="m-0 text-sm text-k-ink-2">¿Desea eliminar la aseguradora {model.deleteLabel}? Esta acción es irreversible.</p>
                </DialogContent>
                <DialogActions className="gap-1 px-6 pb-5">
                    <button type="button" className={`${dialogBtnClass} border border-k-line bg-k-surface text-k-ink hover:bg-k-hover`} onClick={model.cancelDelete}>Cancelar</button>
                    <button type="button" className={`${dialogBtnClass} border-0 bg-k-danger font-bold !text-white hover:brightness-110`} onClick={model.confirmDelete} disabled={model.deleting}>
                        {model.deleting ? 'Eliminando…' : 'Eliminar'}
                    </button>
                </DialogActions>
            </Dialog>

            <div className="flex min-h-0 flex-1 flex-col gap-[18px] bg-k-bg px-[clamp(16px,2.5vw,32px)] pb-10 pt-6">
                {model.loading && !model.initialLoadDone && (
                    <div className="flex min-h-0 flex-1 flex-col gap-[18px]">
                        <div>
                            <div className="mb-2 h-7 w-40 animate-pulse rounded bg-k-line md:h-8 md:w-52" />
                            <div className="hidden h-4 w-64 animate-pulse rounded bg-k-line md:block" />
                        </div>
                        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[18px] border border-k-line bg-k-surface">
                            <div className="shrink-0 border-b border-k-line px-4 py-3.5">
                                <div className="h-[42px] max-w-[380px] animate-pulse rounded-xl bg-k-line" />
                            </div>
                            <table className="w-full table-fixed border-collapse md:min-w-[760px] md:table-auto">
                                {tableHead}
                                <tbody className="bg-k-surface">
                                    {SKELETON_ROWS.map((row) => (
                                        <tr key={row} className="border-t border-k-line">
                                            <td className="px-4 py-2.5">
                                                <div className="flex items-center gap-3">
                                                    <div className="h-[34px] w-[34px] animate-pulse rounded-[10px] bg-k-line" />
                                                    <div>
                                                        <div className="h-3.5 w-44 animate-pulse rounded bg-k-line" />
                                                        <div className="mt-1 h-3 w-16 animate-pulse rounded bg-k-line" />
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="hidden px-2.5 py-2.5 md:table-cell"><div className="h-5 w-24 animate-pulse rounded-full bg-k-line" /></td>
                                            <td className="hidden px-2.5 py-2.5 lg:table-cell"><div className="h-7 w-16 animate-pulse rounded-lg bg-k-line" /></td>
                                            <td className="hidden px-2.5 py-2.5 md:table-cell"><div className="h-5 w-16 animate-pulse rounded-full bg-k-line" /></td>
                                            <td className="hidden px-4 py-2.5 text-right md:table-cell"><div className="ml-auto h-9 w-16 animate-pulse rounded-[10px] bg-k-line" /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {model.error && !model.loading && (
                    <div className="mb-6 rounded-lg border border-k-danger/30 bg-k-danger-soft p-4">
                        <div className="flex items-center">
                            <ErrorOutlineIcon className="mr-2 text-k-danger" style={{ fontSize: 20 }} />
                            <p className="text-sm text-k-danger-ink">{model.error}</p>
                        </div>
                        <Button variant="contained" onClick={model.reload} className="mt-3 !bg-k-danger !text-white">
                            Reintentar
                        </Button>
                    </div>
                )}

                {model.initialLoadDone && !model.error && (
                    <div className="flex min-h-0 flex-1 flex-col gap-[18px]">
                        <header className="flex shrink-0 flex-wrap items-center justify-between gap-3.5 md:items-end">
                            <div className="flex flex-col gap-1">
                                <h1 className="m-0 text-xl font-extrabold tracking-[-0.03em] text-k-ink md:text-[28px]">Aseguradoras</h1>
                                <p className="m-0 hidden text-sm text-k-muted md:block">Catálogo de aseguradoras con las que trabajas · {formatCount(model.totalItems)} registros</p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2.5">
                                <button
                                    type="button"
                                    aria-label="Actualizar"
                                    title="Actualizar"
                                    className="inline-flex h-11 w-11 items-center justify-center gap-2 rounded-xl border border-k-line bg-k-surface text-sm font-semibold text-k-ink hover:bg-k-hover md:w-auto md:px-4"
                                    onClick={model.reload}
                                >
                                    <RefreshIcon className={model.loading ? 'animate-spin' : ''} style={{ fontSize: 18 }} />
                                    <span className="sr-only md:not-sr-only">Actualizar</span>
                                </button>
                                <GrantContent permisos={[Permisos.ASEGURADORAS_W]}>
                                    <Link
                                        to={model.newLocation.pathname}
                                        state={model.newLocation.state}
                                        aria-label="Nueva aseguradora"
                                        title="Nueva aseguradora"
                                        className="inline-flex h-11 w-11 items-center justify-center gap-2 rounded-xl bg-k-primary text-sm font-bold !text-white no-underline hover:brightness-105 hover:!text-white md:w-auto md:px-[18px]"
                                    >
                                        <AddIcon style={{ fontSize: 16 }} />
                                        <span className="sr-only md:not-sr-only">Nueva aseguradora</span>
                                    </Link>
                                </GrantContent>
                            </div>
                        </header>

                        {model.message && (
                            <div className={`flex items-center gap-3 rounded-[14px] px-4 py-3.5 text-sm ${model.message.type === 'error' ? 'bg-k-danger-soft text-k-danger-ink' : 'bg-k-ok-soft text-k-ok'}`}>
                                <ErrorOutlineIcon style={{ fontSize: 20 }} />
                                <p className="m-0 flex-1">{model.message.message}</p>
                                <button type="button" aria-label="Cerrar aviso" className="inline-flex h-7 w-7 items-center justify-center rounded-md border-0 bg-transparent text-inherit hover:bg-black/5" onClick={model.dismissMessage}>
                                    <CloseIcon style={{ fontSize: 16 }} />
                                </button>
                            </div>
                        )}

                        <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[18px] border border-k-line bg-k-surface">
                            <div className="flex shrink-0 flex-wrap items-start gap-2.5 border-b border-k-line px-4 py-3.5">
                                <div className="w-full sm:w-[320px]">
                                    <TextField
                                        id="filterAseguradorasNombre"
                                        label="Nombre"
                                        variant="outlined"
                                        size="small"
                                        value={model.filters.nombre}
                                        slotProps={{ htmlInput: { autoComplete: 'off' } }}
                                        sx={listSearchSx}
                                        onChange={(event) => model.changeNombre(event.target.value)}
                                    />
                                </div>
                                <TextField
                                    id="filterAseguradorasAbreviacion"
                                    label="Abreviación"
                                    variant="outlined"
                                    size="small"
                                    value={model.filters.abreviacion}
                                    slotProps={{ htmlInput: { autoComplete: 'off' } }}
                                    sx={smallSearchSx}
                                    onChange={(event) => model.changeAbreviacion(event.target.value)}
                                />
                                <button
                                    type="button"
                                    aria-pressed={model.filters.soloPropias}
                                    onClick={model.toggleSoloPropias}
                                    className={`inline-flex h-10 items-center gap-1.5 rounded-full border px-3.5 text-[13px] ${model.filters.soloPropias
                                        ? 'border-k-ink bg-k-ink font-bold text-k-surface'
                                        : 'border-k-line-strong bg-k-surface font-semibold text-k-ink-2 hover:bg-k-hover'}`}
                                >
                                    Ocultar predefinidas
                                </button>
                                <button
                                    type="button"
                                    title="Limpiar filtros"
                                    aria-label="Limpiar filtros"
                                    className="ml-auto inline-flex h-10 w-10 shrink-0 items-center justify-center self-start rounded-xl bg-k-danger-soft text-k-danger-ink hover:brightness-95"
                                    onClick={model.clearFilters}
                                >
                                    <CloseIcon style={{ fontSize: 18 }} />
                                </button>
                            </div>

                            <div className={`min-h-0 flex-1 overflow-auto ${model.loading ? 'opacity-60' : ''}`}>
                                <table className="w-full table-fixed border-collapse text-sm md:min-w-[760px] md:table-auto">
                                    {tableHead}
                                    <tbody className="bg-k-surface">
                                        {model.rows.length === 0 && (
                                            <tr>
                                                <td colSpan={columns.length} className="px-4 py-12 text-center text-sm text-k-muted">
                                                    No se encontraron aseguradoras con los filtros aplicados.
                                                </td>
                                            </tr>
                                        )}
                                        {model.rows.map((row) => (
                                            <tr
                                                key={row.id}
                                                className={`border-t border-k-line transition-colors ${row.custom ? 'cursor-pointer hover:bg-k-hover' : ''}`}
                                                onClick={row.custom ? () => model.editInsurer(row.id) : undefined}
                                            >
                                                <td className="max-w-[360px] px-4 py-2.5">
                                                    <div className="flex min-w-0 items-center gap-3 text-left">
                                                        <span className={`flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px] ${row.custom ? 'bg-tone-purple text-tone-purple-ink' : 'bg-tone-blue text-tone-blue-ink'}`}>
                                                            <ApartmentOutlinedIcon style={{ fontSize: 18 }} />
                                                        </span>
                                                        <span className="flex min-w-0 flex-col">
                                                            <span className="truncate font-bold text-k-ink">{row.name}</span>
                                                            <span className="truncate text-xs font-semibold tracking-[0.03em] text-k-ink-2">{row.code}</span>
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="hidden whitespace-nowrap px-2.5 py-2.5 md:table-cell">
                                                    <span className={`rounded-full px-[9px] py-[3px] text-xs font-bold ${row.custom ? 'bg-tone-purple text-tone-purple-ink' : 'bg-k-line-soft text-k-ink-2'}`}>
                                                        {row.custom ? 'Personalizada' : 'Predefinida'}
                                                    </span>
                                                </td>
                                                <td className="hidden whitespace-nowrap px-2.5 py-2.5 lg:table-cell">
                                                    <span className="inline-flex gap-1.5">
                                                        <Capability on={row.pdf} icon={<PictureAsPdfOutlinedIcon style={{ fontSize: 16 }} />} label="traducción de PDF" />
                                                        <Capability on={row.sync} icon={<AutorenewIcon style={{ fontSize: 16 }} />} label="sincronización automática" />
                                                    </span>
                                                </td>
                                                <td className="hidden whitespace-nowrap px-2.5 py-2.5 md:table-cell">
                                                    <span className={`inline-flex items-center gap-1.5 rounded-full px-[9px] py-[3px] text-xs font-bold ${row.active ? 'bg-k-ok-soft text-k-ok' : 'bg-k-danger-soft text-k-danger-ink'}`}>
                                                        <span className={`h-2 w-2 rounded-full ${row.active ? 'bg-k-ok' : 'bg-k-danger'}`} />
                                                        {row.active ? 'Activo' : 'Inactivo'}
                                                    </span>
                                                </td>
                                                <td className="hidden whitespace-nowrap px-4 py-2.5 text-right md:table-cell">
                                                    <RowActions row={row} onEdit={() => model.editInsurer(row.id)} onDelete={() => model.askDelete(row.id)} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-k-line bg-k-surface px-4 py-3 text-sm text-k-muted">
                                <span className="hidden md:inline">Mostrando {formatCount(model.rangeFrom)} a {formatCount(model.rangeTo)} de {formatCount(model.totalItems)} aseguradoras</span>
                                <div className="flex items-center gap-1">
                                    <button
                                        type="button"
                                        aria-label="Página anterior"
                                        disabled={model.page <= 1 || model.loading}
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-k-line bg-k-surface text-k-ink-2 hover:bg-k-hover disabled:opacity-40"
                                        onClick={() => model.setPage(model.page - 1)}
                                    >
                                        <ChevronLeftIcon fontSize="small" />
                                    </button>
                                    {model.pages.map((number) => (
                                        <button
                                            key={number}
                                            type="button"
                                            aria-current={number === model.page ? 'page' : undefined}
                                            disabled={model.loading}
                                            className={`inline-flex h-8 min-w-8 items-center justify-center rounded-md border px-2 text-sm font-medium disabled:opacity-40 ${number === model.page
                                                ? 'border-k-primary bg-k-accent-soft text-k-accent-ink'
                                                : 'border-k-line bg-k-surface text-k-ink-2 hover:bg-k-hover'}`}
                                            onClick={() => model.setPage(number)}
                                        >
                                            {number}
                                        </button>
                                    ))}
                                    <button
                                        type="button"
                                        aria-label="Página siguiente"
                                        disabled={model.page >= model.totalPages || model.loading}
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-k-line bg-k-surface text-k-ink-2 hover:bg-k-hover disabled:opacity-40"
                                        onClick={() => model.setPage(model.page + 1)}
                                    >
                                        <ChevronRightIcon fontSize="small" />
                                    </button>
                                    <Select<number>
                                        size="small"
                                        value={model.pageSize}
                                        disabled={model.loading}
                                        onChange={(event) => model.changePageSize(Number(event.target.value))}
                                        inputProps={{ 'aria-label': 'Registros por página' }}
                                        className="ml-1 h-8 min-w-[72px] rounded-md bg-k-surface text-sm text-k-ink [&_.MuiOutlinedInput-notchedOutline]:border-k-line [&_.MuiSelect-select]:min-h-0 [&_.MuiSelect-select]:py-0 [&_.MuiSelect-select]:leading-8 [&_.MuiSelect-select]:text-k-ink [&_.MuiSvgIcon-root]:text-k-muted"
                                        MenuProps={{
                                            slotProps: {
                                                paper: {
                                                    className: 'rounded-md border border-k-line bg-k-surface text-k-ink [&_.MuiMenuItem-root]:text-k-ink [&_.MuiMenuItem-root.Mui-selected]:bg-k-accent-soft [&_.MuiMenuItem-root.Mui-selected]:text-k-accent-ink',
                                                },
                                            },
                                        }}
                                    >
                                        {PAGE_SIZES.map((size) => (
                                            <MenuItem key={size} value={size}>{size}</MenuItem>
                                        ))}
                                    </Select>
                                </div>
                            </div>
                        </section>
                    </div>
                )}
            </div>
        </KarteraAdminTemplate>
    )
}

export default InsurerList
